import { useState, useEffect, useCallback, useRef } from 'react';
import { productsService } from '@/services/products.service';
import { posDb, type CachedProduct } from '@/db/pos-database';
import { encryptManyForStorage, decryptFromStorage, decryptManyFromStorage } from '@/db/encrypted-access';
import { useNetworkStore } from '@/stores/use-network-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { useLocationStore } from '@/stores/use-location-store';
import type { Product } from '@bbpos/types';
import type { ProductGridItem } from '@bbpos/ui/src/components/POSBillingComponents/ProductGrid/types';

export type ProductChannel = 'pos' | 'sales';

interface UseProductsOptions {
  category?: string;
  pageSize?: number;
  /**
   * Which surface the grid serves: 'pos' (counter billing / restaurant
   * ordering) or 'sales' (quotations). Products the owner hid for that
   * channel are dropped both from the API response and the local cache.
   */
  channel?: ProductChannel;
}

function hiddenForChannel(p: { hide_in_pos?: boolean; hide_in_sales?: boolean }, channel?: ProductChannel): boolean {
  if (!channel) return false;
  return channel === 'pos' ? p.hide_in_pos === true : p.hide_in_sales === true;
}

// ─── Mappers ────────────────────────────────────────────────────────────────

function cachedToProduct(c: CachedProduct): Product {
  return {
    id: c.id,
    tenant_id: c.tenant_id,
    location_id: c.location_id,
    name: c.name,
    sku: c.sku,
    barcode: c.barcode ?? undefined,
    category: c.category ?? undefined,
    unit: c.unit,
    price: c.price,
    cost: c.cost,
    gst_rate: c.gst_rate,
    hsn_code: c.hsn_code ?? undefined,
    stock_quantity: c.stock_quantity,
    low_stock_threshold: 0,
    is_active: c.is_active,
    hide_in_pos: c.hide_in_pos ?? false,
    hide_in_sales: c.hide_in_sales ?? false,
    variant_price_min: c.variant_price_min ?? null,
    variant_price_max: c.variant_price_max ?? null,
    is_variant: false,
    created_at: c.synced_at,
    updated_at: c.synced_at,
  };
}

function toGridItem(p: Product): ProductGridItem {
  return {
    id: p.id,
    name: p.name,
    sku: p.sku,
    price: p.price,
    // Units held for a wastage approval are not sellable (QA IS4-002).
    stock: p.stock_quantity - Number(p.held_quantity ?? 0),
    imageUrl: p.image_url,
    // A PARENT has variants. `is_variant`/`parent_product_id` mark the CHILD,
    // so testing those flagged variants themselves and left the picker empty.
    hasVariants: p.has_variants === true,
    priceMin: p.variant_price_min ?? undefined,
    priceMax: p.variant_price_max ?? undefined,
    ownStock: p.own_stock_quantity,
    isLowStock: p.stock_quantity > 0 && p.stock_quantity <= p.low_stock_threshold,
    categoryId: p.category ?? undefined,
    gstRate: p.gst_rate,
  };
}

function productToCached(p: Product): CachedProduct {
  return {
    id: p.id,
    tenant_id: p.tenant_id,
    location_id: p.location_id,
    name: p.name,
    sku: p.sku,
    barcode: p.barcode ?? null,
    price: p.price,
    cost: p.cost,
    gst_rate: p.gst_rate,
    hsn_code: p.hsn_code ?? null,
    stock_quantity: p.stock_quantity,
    category: p.category ?? null,
    unit: p.unit,
    is_active: p.is_active,
    hide_in_pos: p.hide_in_pos ?? false,
    hide_in_sales: p.hide_in_sales ?? false,
    variant_price_min: p.variant_price_min ?? null,
    variant_price_max: p.variant_price_max ?? null,
    synced_at: new Date().toISOString(),
  };
}

// ─── Local IndexedDB queries ────────────────────────────────────────────────

async function queryLocalProducts(
  tenantId: string,
  locationId: string,
  search?: string,
  cat?: string,
  limit = 50,
  channel?: ProductChannel,
): Promise<CachedProduct[]> {
  if (!tenantId || !locationId) return [];

  const lowerSearch = search?.toLowerCase();

  // Use the tighter three-part index when a category filter is active, falling
  // back to the two-part index for unfiltered queries.
  const base = cat
    ? posDb.products
        .where('[tenant_id+location_id+category]')
        .equals([tenantId, locationId, cat])
        .filter((p) => {
          if (!p.is_active) return false;
          if (hiddenForChannel(p, channel)) return false;
          if (lowerSearch) {
            return (
              p.name.toLowerCase().includes(lowerSearch) ||
              p.sku.toLowerCase().includes(lowerSearch) ||
              (p.barcode?.toLowerCase().includes(lowerSearch) ?? false)
            );
          }
          return true;
        })
    : posDb.products
        .where('[tenant_id+location_id]')
        .equals([tenantId, locationId])
        .filter((p) => {
          if (!p.is_active) return false;
          if (hiddenForChannel(p, channel)) return false;
          if (lowerSearch) {
            return (
              p.name.toLowerCase().includes(lowerSearch) ||
              p.sku.toLowerCase().includes(lowerSearch) ||
              (p.barcode?.toLowerCase().includes(lowerSearch) ?? false)
            );
          }
          return true;
        });

  // Decrypt the `cost` field back onto each row. Other fields stay plaintext
  // and are already visible in the filter/search above.
  return decryptManyFromStorage(await base.limit(limit).toArray());
}

async function getLocalCategories(
  tenantId: string,
  locationId: string,
  channel?: ProductChannel,
): Promise<string[]> {
  if (!tenantId || !locationId) return [];
  const all = await posDb.products
    .where('[tenant_id+location_id]')
    .equals([tenantId, locationId])
    .filter((p) => p.is_active && !hiddenForChannel(p, channel))
    .toArray();
  return distinctCategories(all);
}

function distinctCategories(list: { category?: string | null }[]): string[] {
  return ([...new Set(list.map((p) => p.category).filter(Boolean))] as string[]).sort();
}

/**
 * The cached (IndexedDB) product with this barcode at this tenant+location, or
 * null. Works offline — the billing scanner and Quick-Add both look here first.
 */
export async function findCachedProductByBarcode(
  tenantId: string,
  locationId: string,
  barcode: string,
  channel?: ProductChannel,
): Promise<Product | null> {
  if (!tenantId || !locationId) return null;
  const local = await decryptFromStorage(
    await posDb.products
      .where('[tenant_id+location_id+barcode]')
      .equals([tenantId, locationId, barcode])
      .first(),
  );
  return local && !hiddenForChannel(local, channel) ? cachedToProduct(local) : null;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useProducts({ category, pageSize = 50, channel }: UseProductsOptions = {}) {
  const [products, setProducts] = useState<Product[]>([]);
  const [gridItems, setGridItems] = useState<ProductGridItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Two tab lists: every category of the location's catalogue, and the
  // categories a search found (tagged with that search, so a stale list from
  // an earlier search is never shown for a new one).
  const [localCategories, setLocalCategories] = useState<string[]>([]);
  // API list, used only while the cache has no categories (first load before
  // the catalogue sync). Tagged with its location.
  const [fallbackCategories, setFallbackCategories] = useState<{ locationId: string; cats: string[] }>({
    locationId: '',
    cats: [],
  });
  const fallbackRequestedFor = useRef<string | null>(null);
  const [searchCategories, setSearchCategories] = useState<{ query: string; cats: string[] }>({
    query: '',
    cats: [],
  });
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  // Monotonic request id: a slow response from an earlier fetch (different
  // location / filter) must never overwrite the result of a newer one.
  const requestSeq = useRef(0);
  const tenantId = useAuthStore((s) => s.profile?.tenant_id ?? s.user?.tenant_id ?? '');
  const currentLocationId = useLocationStore((s) => s.currentLocationId) ?? '';
  const isConnected = useNetworkStore((s) => s.isConnected);

  // ── Phase 1: Load from IndexedDB (instant) ───────────────────────────────

  const loadLocal = useCallback(
    async (search: string | undefined, cat: string | undefined, seq: number) => {
      try {
        const cached = await queryLocalProducts(tenantId, currentLocationId, search, cat ?? category, pageSize, channel);
        if (seq !== requestSeq.current) return;
        const mapped = cached.map((c) => cachedToProduct(c));
        setProducts(mapped);
        setGridItems(mapped.map(toGridItem));
        // A search across every category shrinks the tabs to what it found.
        // A search narrowed by a tab must not, or the tabs collapse to one.
        if (search && !(cat ?? category)) {
          setSearchCategories({ query: search, cats: distinctCategories(mapped) });
        }
        // Full tab list from the whole synced catalogue, never from one page.
        const cats = await getLocalCategories(tenantId, currentLocationId, channel);
        if (seq !== requestSeq.current) return;
        setLocalCategories(cats);
        if (cats.length === 0 && isConnected && currentLocationId && fallbackRequestedFor.current !== currentLocationId) {
          const locationId = currentLocationId;
          fallbackRequestedFor.current = locationId;
          productsService
            .getCategories(locationId)
            .then((list) => setFallbackCategories({ locationId, cats: [...list].sort() }))
            .catch(() => {
              fallbackRequestedFor.current = null;
            });
        }
      } catch {
        // IndexedDB unavailable — will rely on API
      }
    },
    [category, pageSize, tenantId, currentLocationId, channel, isConnected],
  );

  // ── Phase 2: Background API refresh (when online) ────────────────────────

  const refreshFromApi = useCallback(
    async (search: string | undefined, cat: string | undefined, seq: number) => {
      // Never fetch without a location: an unscoped request returns the whole
      // tenant catalogue (every store's products) and used to race the scoped
      // one fired once the location hydrated.
      if (!isConnected || !currentLocationId) return;
      try {
        const result = await productsService.list({
          location_id: currentLocationId,
          category: cat ?? category,
          search: search || undefined,
          is_active: 'true',
          channel,
          limit: pageSize,
        });
        if (seq !== requestSeq.current) return;
        const list = result.data;
        setProducts(list);
        setGridItems(list.map(toGridItem));

        // Search tabs from a search across every category. The full tab list
        // is never rebuilt here: this answer holds only one page of products.
        if (search && !(cat ?? category)) {
          setSearchCategories({ query: search, cats: distinctCategories(list) });
        }

        // Write back to IndexedDB to keep cache fresh — skip any product
        // missing authoritative tenant/location context.
        (async () => {
          const cached = list
            .filter((p: Product) => p.tenant_id && p.location_id)
            .map(productToCached);
          await posDb.products.bulkPut(await encryptManyForStorage(cached, 'products'));
        })().catch(() => {});
      } catch {
        // API failed — local data remains displayed, no error shown
      }
    },
    [isConnected, currentLocationId, category, pageSize, channel],
  );


  // ── Combined fetch: local first, then API background refresh ─────────────

  const fetchProducts = useCallback(
    async (search?: string, cat?: string) => {
      const seq = ++requestSeq.current;
      setIsLoading(true);
      setError(null);
      await loadLocal(search, cat, seq);
      if (seq === requestSeq.current) setIsLoading(false);
      // Background refresh — don't await, don't set loading
      refreshFromApi(search, cat, seq);
    },
    [loadLocal, refreshFromApi],
  );

  // Initial load
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts, tenantId, currentLocationId]);

  // Debounced search; `immediate` fetches at once (Enter) and drops a pending one.
  const search = useCallback(
    (query: string, immediate = false) => {
      setSearchQuery(query);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (immediate) {
        fetchProducts(query);
        return;
      }
      searchTimerRef.current = setTimeout(() => {
        fetchProducts(query);
      }, 300);
    },
    [fetchProducts],
  );

  // Drop the search at once (no debounce wait) and show the whole catalogue.
  const clearSearch = useCallback(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    setSearchQuery('');
    fetchProducts();
  }, [fetchProducts]);

  const filterByCategory = useCallback(
    (cat: string | undefined) => {
      fetchProducts(searchQuery, cat);
    },
    [fetchProducts, searchQuery],
  );

  // ── Phase 3: Barcode lookup — local first, API fallback ──────────────────

  const findByBarcode = useCallback(
    async (barcode: string): Promise<Product | null> => {
      // Check IndexedDB first — must match current tenant+location
      try {
        const local = await findCachedProductByBarcode(tenantId, currentLocationId, barcode, channel);
        if (local) return local;
      } catch {
        // IndexedDB unavailable — fall through to API
      }

      // API fallback (only if online)
      if (isConnected) {
        try {
          return await productsService.getByBarcode(barcode, currentLocationId || undefined, channel);
        } catch {
          return null;
        }
      }

      return null;
    },
    [isConnected, tenantId, currentLocationId, channel],
  );

  const refresh = useCallback(() => {
    fetchProducts(searchQuery);
  }, [fetchProducts, searchQuery]);

  const fullCategories =
    localCategories.length > 0
      ? localCategories
      : fallbackCategories.locationId === currentLocationId
        ? fallbackCategories.cats
        : [];
  // Shrink to the search's categories while a search is on; the full list
  // comes back when it clears.
  const categories =
    searchQuery && searchCategories.query === searchQuery ? searchCategories.cats : fullCategories;

  return {
    products,
    gridItems,
    isLoading,
    error,
    categories,
    searchQuery,
    search,
    clearSearch,
    filterByCategory,
    findByBarcode,
    refresh,
  };
}
